import { useEffect, useMemo, useState } from 'react';
import { useCart } from '../../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { useAppData } from '@/context/AppDataContext';
import { compareProductSearchResults, normalizeSearchText, productSearchRank } from '@/utils/search';
import { findLoyaltyClient, type LoyaltyClient } from '@/lib/loyaltyService';
import { useAuth } from '@/context/AuthContext';

type GlobalSearchResult = {
  id: string;
  type: 'producto' | 'cliente' | 'paciente' | 'club';
  title: string;
  subtitle: string;
  icon: string;
  path: string;
};

export default function Header() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [clubClient, setClubClient] = useState<LoyaltyClient | null>(null);
  const [clubSearching, setClubSearching] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const { totalItems } = useCart();
  const { products, clients, patients } = useAppData();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
      if (event.key === 'Escape') setSearchOpen(false);
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, []);

  useEffect(() => {
    const dni = searchQuery.replace(/\D/g, '');
    const isDniQuery = dni.length >= 7 && dni.length <= 11 && !/[a-z]/i.test(searchQuery);
    const alreadyLocal = clients.some((client) => client.dni && client.dni.replace(/\D/g, '') === dni);
    setClubClient(null);
    if (!isDniQuery || alreadyLocal) {
      setClubSearching(false);
      return;
    }

    let cancelled = false;
    setClubSearching(true);
    const timer = window.setTimeout(() => {
      findLoyaltyClient(dni)
        .then((client) => {
          if (!cancelled) setClubClient(client);
        })
        .catch(() => {
          if (!cancelled) setClubClient(null);
        })
        .finally(() => {
          if (!cancelled) setClubSearching(false);
        });
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [clients, searchQuery]);

  const searchResults = useMemo<GlobalSearchResult[]>(() => {
    const query = normalizeSearchText(searchQuery);
    if (!query) return [];

    const productResults = products
      .filter((product) => productSearchRank(product, query) !== null)
      .sort((a, b) => compareProductSearchResults(a, b, query))
      .slice(0, 5)
      .map((product) => ({
        id: product.id,
        type: 'producto' as const,
        title: product.name,
        subtitle: [product.brand, product.category, product.barcode].filter(Boolean).join(' · '),
        icon: 'ri-shopping-bag-3-line',
        path: `/productos?edit=${encodeURIComponent(product.id)}`,
      }));

    const personMatches = (values: unknown[]) =>
      values.some((value) => normalizeSearchText(value).includes(query));

    const clientResults = clients
      .filter((client) => personMatches([client.name, client.dni, client.phone, client.whatsapp, client.email]))
      .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }))
      .slice(0, 3)
      .map((client) => ({
        id: client.id,
        type: 'cliente' as const,
        title: client.name,
        subtitle: [client.dni && `DNI ${client.dni}`, client.phone].filter(Boolean).join(' · '),
        icon: 'ri-user-line',
        path: `/clientes?q=${encodeURIComponent(searchQuery.trim())}`,
      }));

    const patientResults = patients
      .filter((patient) => personMatches([patient.name, patient.clientName, patient.breed, patient.species, patient.chipNumber]))
      .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }))
      .slice(0, 3)
      .map((patient) => ({
        id: patient.id,
        type: 'paciente' as const,
        title: patient.name,
        subtitle: [patient.clientName && `Propietario: ${patient.clientName}`, patient.breed || patient.species].filter(Boolean).join(' · '),
        icon: 'ri-heart-pulse-line',
        path: `/pacientes?q=${encodeURIComponent(searchQuery.trim())}`,
      }));

    const clubResults: GlobalSearchResult[] = clubClient ? [{
      id: `club-${clubClient.id || clubClient.dni}`,
      type: 'club',
      title: `${clubClient.name} ${clubClient.lastName}`.trim() || `DNI ${clubClient.dni}`,
      subtitle: [`DNI ${clubClient.dni}`, clubClient.phone, 'Encontrado en Club NeoZoo'].filter(Boolean).join(' · '),
      icon: 'ri-vip-diamond-line',
      path: `/clientes?q=${encodeURIComponent(clubClient.dni)}&club=${encodeURIComponent(clubClient.dni)}`,
    }] : [];

    return [...clubResults, ...productResults, ...clientResults, ...patientResults].slice(0, 10);
  }, [clients, clubClient, patients, products, searchQuery]);

  const selectResult = (result: GlobalSearchResult) => {
    setSearchOpen(false);
    setSearchQuery('');
    navigate(result.path);
  };

  return (
    <header className="h-14 bg-white border-b border-gray-200 flex items-center px-4 justify-between">
      <div className="flex items-center flex-1 max-w-xl">
        <button
          onClick={() => setSearchOpen(true)}
          className="flex items-center w-full h-9 px-3 bg-gray-100 rounded-lg text-sm text-gray-500 hover:bg-gray-200 transition-colors"
        >
          <div className="w-4 h-4 flex items-center justify-center mr-2">
            <i className="ri-search-line"></i>
          </div>
          <span className="flex-1 text-left">Buscar cliente, paciente o producto...</span>
          <kbd className="ml-2 px-1.5 py-0.5 text-xs bg-white rounded border border-gray-300 text-gray-500">
            Ctrl K
          </kbd>
        </button>
      </div>

      <div className="flex items-center gap-3 ml-4">
        <button
          onClick={() => navigate('/ventas')}
          className="relative w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          title="Ir a Ventas"
        >
          <i className="ri-shopping-cart-line"></i>
          {totalItems > 0 && (
            <span className="absolute -top-1 -right-1 w-4.5 h-4.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
              {totalItems > 9 ? '9+' : totalItems}
            </span>
          )}
        </button>
        <button className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
          <i className="ri-notification-3-line"></i>
        </button>
        <div className="relative">
          <button onClick={() => setProfileOpen((open) => !open)} className="flex items-center gap-2 rounded-lg p-1 pr-2 hover:bg-gray-100 transition-colors" aria-expanded={profileOpen}>
            <div className="w-8 h-8 rounded-full bg-[#0F5132] flex items-center justify-center text-white text-xs font-semibold">
              {user?.fullName.split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'NZ'}
            </div>
            <span className="text-sm text-gray-700 hidden md:block max-w-36 truncate">{user?.fullName}</span>
            <i className="ri-arrow-down-s-line text-gray-400 hidden md:block" />
          </button>
          {profileOpen && (
            <div className="absolute right-0 top-11 w-64 bg-white rounded-xl border border-gray-200 shadow-xl z-50 overflow-hidden animate-slide-in-up">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.fullName}</p>
                <p className="text-xs text-gray-500 truncate mt-0.5">{user?.email}</p>
                <span className="inline-block mt-2 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                  {user?.role === 'admin' ? 'Administrador' : user?.role === 'veterinarian' ? 'Veterinario/a' : 'Vendedor/a'}
                </span>
              </div>
              {user?.role === 'admin' && <button onClick={() => { setProfileOpen(false); navigate('/usuarios'); }} className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"><i className="ri-team-line" />Administrar usuarios</button>}
              <button onClick={() => void logout()} className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-600 hover:bg-red-50 border-t border-gray-100"><i className="ri-logout-box-r-line" />Cerrar sesión</button>
            </div>
          )}
        </div>
      </div>

      {searchOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 bg-black/30" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setSearchOpen(false);
        }}>
          <div className="w-full max-w-xl bg-white rounded-xl shadow-2xl overflow-hidden">
            <div className="flex items-center px-4 py-3 border-b border-gray-100">
              <div className="w-5 h-5 flex items-center justify-center text-gray-400">
                <i className="ri-search-line"></i>
              </div>
              <input
                type="text"
                autoFocus
                className="flex-1 ml-3 outline-none text-sm bg-transparent"
                placeholder="Buscar cliente, paciente o producto..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button
                onClick={() => setSearchOpen(false)}
                className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            {!searchQuery.trim() ? (
              <div className="p-4 text-sm text-gray-500 text-center">
                Escribí para buscar clientes, pacientes o productos
              </div>
            ) : searchResults.length > 0 || clubSearching ? (
              <div className="max-h-[420px] overflow-y-auto py-2">
                {clubSearching && (
                  <div className="flex items-center gap-2 px-4 py-3 text-xs text-gray-500">
                    <i className="ri-loader-4-line animate-spin" />
                    Buscando también en Club NeoZoo…
                  </div>
                )}
                {searchResults.map((result) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    type="button"
                    onClick={() => selectResult(result)}
                    className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${result.type === 'club' ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-[#0F5132]'}`}>
                      <i className={result.icon}></i>
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-gray-900 truncate">{result.title}</span>
                      <span className="block text-xs text-gray-500 truncate">{result.subtitle || result.type}</span>
                    </span>
                    <span className="text-[11px] uppercase tracking-wide text-gray-400">{result.type === 'club' ? 'Club NeoZoo' : result.type}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-6 text-sm text-gray-500 text-center">
                No se encontraron resultados para “{searchQuery.trim()}”.
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
